import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import LayersIcon from '@mui/icons-material/Layers';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RouteIcon from '@mui/icons-material/Route';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import TuneIcon from '@mui/icons-material/Tune';
import { Box } from '@mui/material';

import { withResourceProtection } from 'app/components/ReusableUIComponents/HOCs/ResourceProtected/withResourceProtection';

import { ConfigureModuleCard } from './ConfigureModuleCard';
import { useConfigurePage } from '../hooks/useConfigurePage';
import {
  CONFIGURE_RESOURCE_CALENDAR,
  CONFIGURE_RESOURCE_CONTENT,
  CONFIGURE_RESOURCE_CDP_SETTINGS,
  CONFIGURE_RESOURCE_INVENTORY_FEED_SETTINGS,
  CONFIGURE_RESOURCE_LAUNCH_SETTINGS,
  CONFIGURE_RESOURCE_NURTURING_AUTOMATION,
  CONFIGURE_RESOURCE_NURTURING_JOURNEY,
  CONFIGURE_RESOURCE_NURTURING_PLAN_GENERATOR,
  CONFIGURE_RESOURCE_SEGMENT_MESSAGE_TYPE_FOCUS,
  CONFIGURE_RESOURCE_SEGMENTS,
  CONFIGURE_RESOURCE_STATIC_VALUE_MESSAGES,
  CONFIGURE_RESOURCE_TEMPLATE_VALUE_PREFERENCES,
} from '../utils/configureResourceIds';

const ContentCardInner = () => {
  const { goToContent } = useConfigurePage();
  return (
    <ConfigureModuleCard
      title="Content"
      description="Manage content library for nurturing workflows"
      icon={<DescriptionIcon fontSize="small" />}
      onOpen={goToContent}
      tourId="configure-content"
    />
  );
};

const CalendarCardInner = () => {
  const { goToCalendarConfiguration } = useConfigurePage();
  return (
    <ConfigureModuleCard
      title="Calendar"
      description="Configure timing, cadence, and blackout windows"
      icon={<CalendarTodayIcon fontSize="small" />}
      onOpen={goToCalendarConfiguration}
      tourId="configure-calendar"
    />
  );
};

const SegmentsCardInner = () => {
  const { goToSegmentsConfiguration } = useConfigurePage();
  return (
    <ConfigureModuleCard
      title="Segments"
      description="Define and maintain audience segments"
      icon={<PeopleOutlineIcon fontSize="small" />}
      onOpen={goToSegmentsConfiguration}
      tourId="configure-segments"
    />
  );
};

const TemplateValueCardInner = () => {
  const { goToTemplateValuePreferences } = useConfigurePage();
  return (
    <ConfigureModuleCard
      title="Template Value Preferences"
      description="Set value-messaging preferences for templates"
      icon={<TuneIcon fontSize="small" />}
      onOpen={goToTemplateValuePreferences}
      tourId="configure-template-value"
    />
  );
};

const SegmentMessageCardInner = () => {
  const { goToTemplateMessageTypeFocus } = useConfigurePage();
  return (
    <ConfigureModuleCard
      title="Segment Message Type Focus"
      description="Configure message type emphasis per segment"
      icon={<ChatBubbleOutlineIcon fontSize="small" />}
      onOpen={goToTemplateMessageTypeFocus}
      tourId="configure-segment-message-type"
    />
  );
};

const StaticValueCardInner = () => {
  const { goToStaticContentValueMessages } = useConfigurePage();
  return (
    <ConfigureModuleCard
      title="Static Content - Value Messages"
      description="Maintain static value-message content"
      icon={<LayersIcon fontSize="small" />}
      onOpen={goToStaticContentValueMessages}
      tourId="configure-static-value-messages"
    />
  );
};

const NurturingJourneyCardInner = () => {
  const { goToNurturingJourneySettings } = useConfigurePage();
  return (
    <ConfigureModuleCard
      title="Nurturing Journey"
      description="Manage nurturing journey steps, stages, and segments"
      icon={<RouteIcon fontSize="small" />}
      onOpen={goToNurturingJourneySettings}
      tourId="configure-nurturing-journey"
    />
  );
};

const CdpSettingsCardInner = () => {
  const { goToCdpSettings } = useConfigurePage();
  return (
    <ConfigureModuleCard
      title="CDP Settings"
      description="Configure CRM settings and upload CDP raw lead data"
      icon={<CloudUploadIcon fontSize="small" />}
      onOpen={goToCdpSettings}
      tourId="configure-cdp-settings"
    />
  );
};

const NurturingPlanGeneratorCardInner = () => {
  const { goToNurturingPlanGenerator, isV2Dealership } = useConfigurePage();
  return (
    <ConfigureModuleCard
      title="Nurturing Plan Generator"
      description="Generate and manage nurturing plans for dealerships"
      icon={<AutoAwesomeIcon fontSize="small" />}
      onOpen={goToNurturingPlanGenerator}
      openDisabled={!isV2Dealership}
      tourId="configure-nurturing-plan-generator"
    />
  );
};

const LaunchSettingsCardInner = () => {
  const { goToLaunchSettings, isV2Dealership } = useConfigurePage();
  return (
    <ConfigureModuleCard
      title="Launch Settings"
      description="Configure launch thresholds, look back date, and go-live"
      icon={<RocketLaunchIcon fontSize="small" />}
      onOpen={goToLaunchSettings}
      openDisabled={!isV2Dealership}
      tourId="configure-launch-settings"
    />
  );
};

const NurturingAutomationCardInner = () => {
  const { goToNurturingAutomation } = useConfigurePage();
  return (
    <ConfigureModuleCard
      title="Nurturing Automation"
      description="Enable or disable continuous nurturing automation"
      icon={<PlayCircleOutlineIcon fontSize="small" />}
      onOpen={goToNurturingAutomation}
      tourId="configure-nurturing-automation"
    />
  );
};

const InventoryFeedSettingsCardInner = () => {
  const { goToInventoryFeedSettings } = useConfigurePage();
  return (
    <ConfigureModuleCard
      title="Inventory Feed Settings"
      description="Configure inventory feed store mapping for this dealership"
      icon={<Inventory2Icon fontSize="small" />}
      onOpen={goToInventoryFeedSettings}
      tourId="configure-inventory-feed-settings"
    />
  );
};

const ProtectedContentCard = withResourceProtection(
  ContentCardInner,
  CONFIGURE_RESOURCE_CONTENT,
  null,
);
const ProtectedCalendarCard = withResourceProtection(
  CalendarCardInner,
  CONFIGURE_RESOURCE_CALENDAR,
  null,
);
const ProtectedSegmentsCard = withResourceProtection(
  SegmentsCardInner,
  CONFIGURE_RESOURCE_SEGMENTS,
  null,
);
const ProtectedTemplateValueCard = withResourceProtection(
  TemplateValueCardInner,
  CONFIGURE_RESOURCE_TEMPLATE_VALUE_PREFERENCES,
  null,
);
const ProtectedSegmentMessageCard = withResourceProtection(
  SegmentMessageCardInner,
  CONFIGURE_RESOURCE_SEGMENT_MESSAGE_TYPE_FOCUS,
  null,
);
const ProtectedStaticValueCard = withResourceProtection(
  StaticValueCardInner,
  CONFIGURE_RESOURCE_STATIC_VALUE_MESSAGES,
  null,
);
const ProtectedNurturingJourneyCard = withResourceProtection(
  NurturingJourneyCardInner,
  CONFIGURE_RESOURCE_NURTURING_JOURNEY,
  null,
);
const ProtectedCdpSettingsCard = withResourceProtection(
  CdpSettingsCardInner,
  CONFIGURE_RESOURCE_CDP_SETTINGS,
  null,
);
const ProtectedNurturingPlanGeneratorCard = withResourceProtection(
  NurturingPlanGeneratorCardInner,
  CONFIGURE_RESOURCE_NURTURING_PLAN_GENERATOR,
  null,
);
const ProtectedLaunchSettingsCard = withResourceProtection(
  LaunchSettingsCardInner,
  CONFIGURE_RESOURCE_LAUNCH_SETTINGS,
  null,
);
const ProtectedNurturingAutomationCard = withResourceProtection(
  NurturingAutomationCardInner,
  CONFIGURE_RESOURCE_NURTURING_AUTOMATION,
  null,
);
const ProtectedInventoryFeedSettingsCard = withResourceProtection(
  InventoryFeedSettingsCardInner,
  CONFIGURE_RESOURCE_INVENTORY_FEED_SETTINGS,
  null,
);

export const ConfigureModules = () => {
  return (
    <Box
      data-guidance-tour="configure-modules-grid"
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
        },
        gap: 3,
        width: '100%',
        maxWidth: 1200,
        mx: 'auto',
        alignContent: 'start',
      }}
    >
      <ProtectedContentCard />
      <ProtectedCalendarCard />
      <ProtectedSegmentsCard />
      <ProtectedTemplateValueCard />
      <ProtectedSegmentMessageCard />
      <ProtectedStaticValueCard />
      <ProtectedNurturingJourneyCard />
      <ProtectedCdpSettingsCard />
      <ProtectedNurturingPlanGeneratorCard />
      <ProtectedLaunchSettingsCard />
      <ProtectedInventoryFeedSettingsCard />
      <ProtectedNurturingAutomationCard />
    </Box>
  );
};
